// ABOUTME: Defines bulk-list loading limits and bounded request payloads.
// ABOUTME: Keeps over-limit selections from loading candidate data into the browser.
import type { BulkActionsType, IBulkActions } from "./bulkActionCatalog";

export const bulkProtocolVersion = 1;

export interface IFilteredTotal {
    value?: number;
    relation?: "eq" | "gte";
    actionLimit?: number;
}

const getBulkActionLimit = (
    bulkActionsSettings: Compleo.IObject,
    bulkType: IBulkActions
) => {
    const unlimitedBulkTypes: BulkActionsType[] = ["applicant_importFromFile"];
    if (unlimitedBulkTypes.includes(bulkType.name)) {
        return 1000000000;
    }

    const defaultMaxItems = 1000;
    const stageActionTypes = [
        "applicantjob_addToStage",
        "applicantjob_disqualify",
        "applicantjob_requalify",
        "applicantjob_changeRejectedStage"
    ];
    if (stageActionTypes.includes(bulkType.name)) {
        const configuredLimits = stageActionTypes
            .map(type => bulkActionsSettings?.maxItems?.[type])
            .filter(
                (value: unknown): value is number =>
                    Number.isSafeInteger(value) && value > 0
            );
        return configuredLimits.length
            ? Math.min(...configuredLimits)
            : defaultMaxItems;
    }
    const metadataActionName =
        bulkType.name === "applicantjob_exportToFile"
            ? "applicant_exportToFile"
            : bulkType.name;
    return (
        (bulkActionsSettings?.maxItems || {})[metadataActionName] ||
        defaultMaxItems
    );
};

const getBulkRequestScope = (
    filters: Compleo.IObject,
    companyId: number,
    otherGenericParams: Compleo.IObject
) => ({
    ...filters,
    companyId,
    otherGenericParams: {
        ...(filters.otherGenericParams || {}),
        ...otherGenericParams
    }
});

export const getBulkActionLimitStatus = (
    bulkActionsSettings: Compleo.IObject,
    bulkType: IBulkActions,
    currentFilterTotal: IFilteredTotal
) => {
    const serverLimit = currentFilterTotal?.actionLimit;
    const limit =
        typeof serverLimit === "number" && Number.isSafeInteger(serverLimit)
            ? serverLimit
            : getBulkActionLimit(bulkActionsSettings, bulkType);
    const filtered = currentFilterTotal?.value || 0;
    const exceeded =
        filtered > limit ||
        (currentFilterTotal?.relation === "gte" && filtered >= limit);

    return { limit, filtered, exceeded };
};

export const getBulkStepLimitStatus = (
    bulkActionsSettings: Compleo.IObject,
    bulkType: IBulkActions,
    stepBulk: number,
    currentFilterTotal: IFilteredTotal,
    materializedItemCount: number
) =>
    getBulkActionLimitStatus(
        bulkActionsSettings,
        bulkType,
        stepBulk === 2
            ? {
                  value: materializedItemCount,
                  relation: "eq",
                  actionLimit: currentFilterTotal.actionLimit
              }
            : currentFilterTotal
    );

export const shouldLoadBulkList = (
    stepBulk: number,
    bulkType?: BulkActionsType
) =>
    stepBulk === 2 && bulkType !== "applicant_importFromFile";

export const shouldRenderBulkActionsModal = (open: boolean) => open;

export const getBulkListFields = (
    loadList: boolean,
    fields: Compleo.IObject[] = []
) => (loadList ? fields : []);

export const getBulkListQueryOptions = () => ({
    cacheTime: 0,
    abortOnUnmount: true,
    retry: false
});

export const shouldLoadBulkTotal = (bulkType?: BulkActionsType) =>
    bulkType !== undefined && bulkType !== "applicant_importFromFile";

export const isStaleAIMatchSelectionError = (error: unknown) =>
    (error as { response?: { data?: { code?: unknown } } })?.response?.data
        ?.code === "BulkSelectionJobRelationStale";

export const getAIMatchSubmissionErrorResponse = (error: unknown) =>
    isStaleAIMatchSelectionError(error)
        ? { status: 999 }
        : (error as { response?: unknown })?.response;

export const hasBulkProtocolVersion = (responseProtocolVersion?: unknown) =>
    responseProtocolVersion === bulkProtocolVersion;

export const isBulkSelectionResponseReady = (response?: {
    bulkProtocolVersion?: unknown;
    bulkSelectionId?: unknown;
    fields?: unknown[];
    totalFiltered?: IFilteredTotal;
}) =>
    hasBulkProtocolVersion(response?.bulkProtocolVersion) &&
    (typeof response?.bulkSelectionId === "string" ||
        (response?.fields?.length === 0 &&
            response.totalFiltered?.value === 0 &&
            response.totalFiltered.relation === "eq"));

export const isBulkTotalReady = (
    total?: IFilteredTotal,
    responseProtocolVersion?: unknown
) =>
    hasBulkProtocolVersion(responseProtocolVersion) &&
    typeof total?.value === "number" &&
    (total.relation === "eq" || total.relation === "gte");

export const getBulkListCountParams = (
    filters: Compleo.IObject,
    companyId: number,
    bulkActionType?: BulkActionsType,
    otherGenericParams: Compleo.IObject = {}
) => ({
    ...getBulkRequestScope(filters, companyId, otherGenericParams),
    returnAllItems: false,
    returnAggs: false,
    returnAggregationsAfterQuery: false,
    returnExactTotalFiltered: true,
    bulkProtocolVersion,
    ...(bulkActionType ? { bulkActionType } : {}),
    fieldsToReturn: ["pk"],
    pagination: { pageSize: 1, currentPage: 1 }
});

export const getBulkListParams = (
    filters: Compleo.IObject,
    companyId: number,
    fieldsToReturn: string[],
    bulkActionType: BulkActionsType,
    otherGenericParams: Compleo.IObject = {}
) => ({
    ...getBulkRequestScope(filters, companyId, otherGenericParams),
    returnAllItems: true,
    returnAggs: false,
    bulkActionType,
    bulkProtocolVersion,
    fieldsToReturn
});

export const isStaleEmailSelectionError = (error: unknown) =>
    ["BulkSelectionContextStale", "BulkSelectionJobRelationStale"].includes(
        String((error as { response?: { data?: { code?: unknown } } })?.response?.data?.code)
    );
